import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LogOut, X } from 'lucide-react'
import SetupChatShell from '../components/setup/SetupChatShell'
import SetupMessageTimeline from '../components/setup/SetupMessageTimeline'
import SetupComposer from '../components/setup/SetupComposer'
import SetupProgressPanel from '../components/setup/SetupProgressPanel'
import { getConnectZoomUrl, setupAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'

function getSetupGreetingName(user) {
  if (!user) return 'there'
  const full = (user.name || user.full_name || '').trim()
  if (full) return full.split(/\s+/)[0]
  const email = (user.email || '').trim()
  if (email.includes('@')) return email.split('@')[0]
  return 'there'
}

function createAssistantWelcomeMessage(nameForGreeting) {
  const name = (nameForGreeting || 'there').trim() || 'there'
  return {
    id: 'setup-welcome',
    role: 'assistant',
    content:
      `Hey ${name}! I'm here to set up Rithmo for your meetings. I'll ask a few quick questions about your role, your team, and how you'd like meetings to run.\n\nLet's start — what's your role, and what kind of meetings do you typically run?`,
  }
}

/** Keeps chat history but upgrades the seeded welcome copy when it is the only message. */
function withRefreshedSoloWelcome(messages, greetingName) {
  if (!Array.isArray(messages) || messages.length !== 1 || messages[0]?.id !== 'setup-welcome') {
    return messages
  }
  const content = createAssistantWelcomeMessage(greetingName).content
  if (messages[0].content === content) return messages
  return [{ ...messages[0], content }]
}

function ChatFirstSetupPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const greetingName = getSetupGreetingName(user)
  const userRef = useRef(user)
  userRef.current = user
  const [messages, setMessages] = useState(() => [createAssistantWelcomeMessage('there')])
  const [isLoadingDraft, setIsLoadingDraft] = useState(true)
  const [isSavingMessage, setIsSavingMessage] = useState(false)
  const [isCompletingSetup, setIsCompletingSetup] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [completionError, setCompletionError] = useState('')
  const [isCompleted, setIsCompleted] = useState(false)
  const [turnState, setTurnState] = useState({
    readyForSummary: false,
    structuredUiCard: 'none',
  })
  const [nextActions, setNextActions] = useState([])
  const [persona, setPersona] = useState('explorer')
  const [extractedProfile, setExtractedProfile] = useState({})
  const [showSkipDialog, setShowSkipDialog] = useState(false)
  const [showZoomConnect, setShowZoomConnect] = useState(false)
  const [delegateEmail, setDelegateEmail] = useState('')
  const [isSendingDelegateInvite, setIsSendingDelegateInvite] = useState(false)
  const [delegateInviteMessage, setDelegateInviteMessage] = useState('')

  useEffect(() => {
    let isMounted = true

    const loadDraft = async () => {
      setIsLoadingDraft(true)
      setSaveError('')
      try {
        const response = await setupAPI.getDraft()
        const draft = response?.data?.draft ?? response?.draft
        const draftMessages = Array.isArray(draft?.messages) ? draft.messages : []
        const draftProfile =
          draft && typeof draft.extractedProfile === 'object' && draft.extractedProfile !== null
            ? draft.extractedProfile
            : {}
        if (!isMounted) return
        if (draftMessages.length > 0) {
          setMessages(withRefreshedSoloWelcome(draftMessages, getSetupGreetingName(userRef.current)))
        } else {
          setMessages([createAssistantWelcomeMessage(getSetupGreetingName(userRef.current))])
        }
        setIsCompleted(draft?.status === 'completed')
        setExtractedProfile(draftProfile)
        setTurnState({
          readyForSummary: draftProfile?.readyForSummary === true,
          structuredUiCard: draftProfile?.readyForSummary === true ? 'summaryApproval' : 'none',
        })
        setPersona(
          draftProfile?.persona ??
            draftProfile?.branch ??
            'explorer'
        )
        if (draft?.status === 'completed') {
          try {
            const nextActionsResponse = await setupAPI.getNextActions()
            const nextActionsPayload = nextActionsResponse?.data ?? nextActionsResponse
            setNextActions(Array.isArray(nextActionsPayload?.actions) ? nextActionsPayload.actions : [])
            if (nextActionsPayload?.persona) {
              setPersona(nextActionsPayload.persona)
            }
          } catch {
            setNextActions([])
          }
        }
      } catch {
        if (!isMounted) return
        setMessages([createAssistantWelcomeMessage(getSetupGreetingName(userRef.current))])
        setSaveError('Could not load saved setup draft. You can keep typing and try again.')
      } finally {
        if (isMounted) {
          setIsLoadingDraft(false)
        }
      }
    }

    void loadDraft()
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0]?.id !== 'setup-welcome') return prev
      const next = createAssistantWelcomeMessage(greetingName)
      if (prev[0].content === next.content) return prev
      return [next]
    })
  }, [greetingName])

  const handleSendMessage = async (content) => {
    const optimisticId = `local-${Date.now()}`
    setSaveError('')
    setMessages((previous) => [
      ...previous,
      {
        id: optimisticId,
        role: 'user',
        content,
      },
      {
        id: 'typing-indicator',
        role: 'assistant',
        content: '',
        isTyping: true,
      },
    ])

    setIsSavingMessage(true)
    try {
      const response = await setupAPI.postTurn(content)
      const payload = response?.data ?? response
      const draft = payload?.draft
      const draftMessages = Array.isArray(draft?.messages) ? draft.messages : []
      if (draftMessages.length > 0) {
        setMessages(draftMessages)
      }
      setIsCompleted(draft?.status === 'completed')
      if (payload?.extractedProfile && typeof payload.extractedProfile === 'object') {
        setExtractedProfile(payload.extractedProfile)
      }
      setTurnState({
        readyForSummary: payload?.turn?.readyForSummary === true,
        structuredUiCard: payload?.turn?.structuredUiCard ?? 'none',
      })
      if (payload?.fallbackUsed) {
        setSaveError('The message was saved, but analysis is temporarily limited. You can continue.')
      }
    } catch (error) {
      setMessages((previous) => previous.filter((message) => message.id !== optimisticId))
      const message =
        error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : ''
      const status = error && typeof error === 'object' && 'status' in error ? error.status : undefined
      if (status === 409 && message.toLowerCase().includes('already complete')) {
        setSaveError(message)
        setIsCompleted(true)
      } else if (message.toLowerCase().includes('thread')) {
        setSaveError('This setup session is out of date. Please refresh the page.')
      } else if (message.toLowerCase().includes('temporarily unavailable')) {
        setSaveError('Setup chat history needs the latest migration. Please ask an admin to run DB migrations.')
      } else if (message) {
        setSaveError(message)
      } else {
        setSaveError('Message was not saved. Please try sending it again.')
      }
    } finally {
      setIsSavingMessage(false)
    }
  }

  const canApproveSummary = turnState.readyForSummary || turnState.structuredUiCard === 'summaryApproval'

  const handleApproveSummary = async () => {
    setCompletionError('')
    setIsCompletingSetup(true)
    try {
      const response = await setupAPI.complete({ approved: true })
      const payload = response?.data ?? response
      const draft = payload?.draft
      const draftMessages = Array.isArray(draft?.messages) ? draft.messages : []
      if (draftMessages.length > 0) {
        setMessages(draftMessages)
      }
      setIsCompleted(draft?.status === 'completed')
      if (draft?.status === 'completed') {
        setShowZoomConnect(true)
      }
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : ''
      if (message.includes('not ready to complete')) {
        setCompletionError('Please add one more concrete setup detail, then approve summary again.')
      } else if (message) {
        setCompletionError(message)
      } else {
        setCompletionError('Could not complete setup yet. Please try again.')
      }
    } finally {
      setIsCompletingSetup(false)
    }
  }

  const handleDelegateInvite = async () => {
    const email = delegateEmail.trim()
    if (!email) {
      setDelegateInviteMessage('Enter an email to send the invite.')
      return
    }
    setIsSendingDelegateInvite(true)
    setDelegateInviteMessage('')
    try {
      const response = await setupAPI.postDelegateInvite(email)
      const payload = response?.data ?? response
      if (payload?.delegated) {
        setDelegateInviteMessage(`Invite sent to ${payload?.invite?.email || email}.`)
        setDelegateEmail('')
      } else {
        setDelegateInviteMessage('Invite could not be sent. Please try again.')
      }
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'Invite could not be sent. Please try again.'
      setDelegateInviteMessage(message)
    } finally {
      setIsSendingDelegateInvite(false)
    }
  }

  const connectZoomUrl = getConnectZoomUrl(user?.id)
  const showNextActions = isCompleted && Array.isArray(nextActions) && nextActions.length > 0
  const showInlineActions = (!isCompleted && canApproveSummary) || showNextActions
  const displayName = user?.name || user?.full_name || 'User'
  const initials = (displayName || user?.email || 'U')
    .split(' ')
    .map((segment) => segment?.[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const handleSignOut = async () => {
    await logout()
    navigate('/')
  }

  // #region agent log
  useLayoutEffect(() => {
    const shell = document.querySelector('[data-debug-setup-shell]')
    const composer = document.querySelector('[data-debug-setup-composer]')
    const userFooter = document.querySelector('[data-debug-setup-user-footer]')
    if (!shell || !composer || !userFooter) return
    const sr = shell.getBoundingClientRect()
    const cr = composer.getBoundingClientRect()
    const ur = userFooter.getBoundingClientRect()
    const docEl = document.documentElement
    fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '48d430' },
      body: JSON.stringify({
        sessionId: '48d430',
        runId: 'pre-fix',
        hypothesisId: 'H1-H5',
        location: 'ChatFirstSetupPage.jsx:layout-measure',
        message: 'setup widget layout metrics',
        data: {
          deltaBorderTopPx: Math.round((cr.top - ur.top) * 100) / 100,
          composerHeightPx: Math.round(cr.height * 100) / 100,
          userFooterHeightPx: Math.round(ur.height * 100) / 100,
          composerBottomToShellBottomPx: Math.round((sr.bottom - cr.bottom) * 100) / 100,
          hasSaveError: Boolean(saveError),
          hasCompletionError: Boolean(completionError),
          isCompleted,
          showInlineActions,
          pageScrollable: docEl.scrollHeight > window.innerHeight + 2,
          docScrollHeight: Math.round(docEl.scrollHeight),
          windowInnerHeight: Math.round(window.innerHeight),
          shellHeightPx: Math.round(sr.height * 100) / 100,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {})
  }, [
    saveError,
    completionError,
    isCompleted,
    showInlineActions,
    messages.length,
    isLoadingDraft,
  ])
  // #endregion

  const suggestionChips = useMemo(() => {
    const has = (k) => {
      const v = extractedProfile[k]
      if (v == null) return false
      if (typeof v === 'string') return v.trim().length > 0
      if (Array.isArray(v)) return v.length > 0
      return false
    }
    const chips = []
    if (!has('desiredOutcomes')) {
      chips.push('I want meetings to end on time')
      chips.push('I want better participation from everyone')
    }
    if (!has('meetingPainPoints')) {
      chips.push('My meetings tend to go off-topic')
      chips.push('People show up late to my meetings')
    }
    if (!has('directnessPreference')) {
      chips.push('Be direct with my team')
      chips.push('Keep it subtle and gentle')
    }
    if (!has('operatingPrinciples')) {
      chips.push('Stick to the agenda strictly')
    }
    return chips.slice(0, 4)
  }, [extractedProfile])

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 relative overflow-x-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
      <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />

      <Link to="/" className="absolute top-5 left-6 z-10 flex items-center gap-2">
        <img src="/rithmo-mark.png" alt="Rithmo" className="w-8 h-8 object-contain" />
        <span className="text-lg font-bold text-gray-900">Rithmo</span>
      </Link>

      <div className="absolute top-5 right-6 z-10 flex items-center gap-4">
        <button
          type="button"
          onClick={() => setShowSkipDialog(true)}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium transition"
        >
          Skip to home →
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="text-sm text-gray-400 hover:text-red-500 transition"
        >
          Sign out
        </button>
      </div>

      <main className="h-[calc(100vh-64px)] mt-16 p-3 lg:p-4 max-w-6xl mx-auto w-full">
        <SetupChatShell rightPanel={messages.some(m => m.role === 'user') ? <SetupProgressPanel extractedProfile={extractedProfile} isCompleted={isCompleted} /> : null}>
          <div className="flex min-h-0 flex-1 flex-col">
            {saveError ? (
              <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
                {saveError}
              </div>
            ) : null}
            {completionError ? (
              <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
                {completionError}
              </div>
            ) : null}
            <SetupMessageTimeline messages={messages} />
            {isCompleted ? (
              <div className="border-t border-gray-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                Setup is complete. Your preferences are saved — chat editing is turned off. Use the actions below or open the dashboard.
              </div>
            ) : null}
            {showInlineActions ? (
              <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-700">
                  Suggested actions
                </p>
                {!isCompleted && canApproveSummary ? (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={handleApproveSummary}
                      disabled={isCompletingSetup}
                      className="btn-primary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {isCompletingSetup ? 'Completing setup...' : 'Approve summary and finish setup'}
                    </button>
                  </div>
                ) : null}
                {showNextActions ? (
                  <>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {nextActions.map((action) => {
                        if (action.type === 'openDashboard') {
                          return (
                            <Link
                              key={action.id}
                              to="/home"
                              className={`text-sm ${action.enabled ? 'btn-primary px-3 py-2' : 'pointer-events-none rounded-md border border-gray-200 px-3 py-2 text-gray-400'}`}
                            >
                              {action.label}
                            </Link>
                          )
                        }
                        if (action.type === 'connectZoom') {
                          if (action.connected) {
                            return (
                              <span key={action.id} className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                                Zoom connected
                              </span>
                            )
                          }
                          return (
                            <a
                              key={action.id}
                              href={connectZoomUrl || '#'}
                              className={`text-sm ${action.enabled ? 'btn-primary px-3 py-2' : 'cursor-not-allowed rounded-md border border-gray-200 px-3 py-2 text-gray-400'}`}
                            >
                              Connect Zoom
                            </a>
                          )
                        }
                        if (action.type === 'openQuickStart') {
                          return (
                            <Link
                              key={action.id}
                              to={action.destination || '/agenda/quick-start'}
                              className={`text-sm ${action.enabled ? 'btn-primary px-3 py-2' : 'pointer-events-none rounded-md border border-gray-200 px-3 py-2 text-gray-400'}`}
                            >
                              {action.label}
                            </Link>
                          )
                        }
                        return null
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
            <SetupComposer
              onSend={handleSendMessage}
              disabled={isLoadingDraft || isSavingMessage || isCompleted}
              suggestions={isCompleted ? [] : suggestionChips}
            />
          </div>
        </SetupChatShell>
      </main>

      {showSkipDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Skip setup?</h3>
              <button onClick={() => setShowSkipDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Completing setup helps Rithmo understand your role, team, and meeting style so it can tailor its behavior to how you actually work. Without it, you'll get a generic experience.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSkipDialog(false)}
                className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition"
              >
                Continue setup
              </button>
              <button
                onClick={() => navigate('/home')}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Skip anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showZoomConnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Setup complete!</h3>
            <p className="text-sm text-gray-600 mb-6">
              One last step — connect your Zoom account so Rithmo can join and manage your meetings.
            </p>
            {(() => {
              const zoomUrl = getConnectZoomUrl(user?.id)
              return zoomUrl ? (
                <a
                  href={zoomUrl}
                  className="inline-block w-full px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-cyan-600 rounded-xl hover:opacity-90 transition"
                >
                  Connect Zoom Account
                </a>
              ) : (
                <button
                  onClick={() => navigate('/home')}
                  className="w-full px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-cyan-600 rounded-xl hover:opacity-90 transition"
                >
                  Go to Dashboard
                </button>
              )
            })()}
            <button
              onClick={() => navigate('/home')}
              className="mt-3 text-sm text-gray-400 hover:text-gray-600 transition"
            >
              I'll do this later
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatFirstSetupPage
